import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n/i18n';
import { ABM_ASSIGNMENT } from '@/../../shared/types/inventory';
import { UserRound, Mail, ShieldCheck, Apple, Laptop, Heart, type LucideIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface AltaPipeline {
  estado: string | null;
  error_mensaje: string | null;
  fecha_procesado: string | null;
  welcome_email: string | null;
  ev_email: string | null;
  ev_email_sent_at: string | null;
  security_group: string | null;
  security_group_at: string | null;
  abm_assignment: string | null;
  enrollment_reminder: string | null;
  // Mails de onboarding de People (manager / primeros pasos / día de ingreso)
  people_manager_mail?: string | null;
  people_first_steps_mail?: string | null;
  people_admission_mail?: string | null;
}

type StepStatus = 'ok' | 'scheduled' | 'waiting' | 'error' | 'na';

export const STEP_CLASSES: Record<StepStatus, string> = {
  ok: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400',
  scheduled: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-400',
  waiting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-400',
  error: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400',
  na: 'bg-muted text-muted-foreground/50',
};

const STEP_ICONS: Record<string, LucideIcon> = {
  account: UserRound,
  mails: Mail,
  people_mails: Heart,
  security: ShieldCheck,
  apple: Apple,
  enrollment: Laptop,
};

const isError = (v: string | null) => !!v && v.startsWith('error');

export function getPipelineSteps(pipeline: AltaPipeline | null, hasCorreo: boolean) {
  const steps: { key: string; status: StepStatus; detail: string }[] = [];

  // 1. Cuenta Google
  if (!pipeline) {
    steps.push({ key: 'account', status: hasCorreo ? 'ok' : 'waiting', detail: '' });
  } else if (pipeline.estado === 'error') {
    steps.push({ key: 'account', status: 'error', detail: pipeline.error_mensaje ?? '' });
  } else if (pipeline.estado === 'resuelto_manual') {
    steps.push({ key: 'account', status: 'ok', detail: 'resuelto manualmente' });
  } else {
    steps.push({ key: 'account', status: 'ok', detail: pipeline.fecha_procesado ?? '' });
  }

  // 2. Mails (bienvenida + EV)
  if (!pipeline || (!pipeline.welcome_email && !pipeline.ev_email)) {
    steps.push({ key: 'mails', status: 'na', detail: '' });
  } else if (isError(pipeline.welcome_email) || isError(pipeline.ev_email)) {
    steps.push({
      key: 'mails',
      status: 'error',
      detail: [pipeline.welcome_email, pipeline.ev_email].filter(isError).join(' | '),
    });
  } else if (pipeline.welcome_email === 'programado' || pipeline.ev_email === 'programado') {
    steps.push({ key: 'mails', status: 'scheduled', detail: '' });
  } else {
    steps.push({ key: 'mails', status: 'ok', detail: '' });
  }

  // 2.5. Mails de People (aviso al manager / primeros pasos / día de ingreso)
  // `i18n.t` y no el hook: esto es una función de modelo (también la usa flow-model.ts),
  // no un componente. Se evalúa al render, así que sigue el idioma activo.
  {
    const pm = pipeline?.people_manager_mail ?? null;
    const fs = pipeline?.people_first_steps_mail ?? null;
    const ad = pipeline?.people_admission_mail ?? null;
    const all = [
      { label: 'manager', v: pm },
      { label: i18n.t('users.onboardingpipeline.primeros_pasos'), v: fs },
      { label: i18n.t('users.onboardingpipeline.dia_ingreso'), v: ad },
    ];
    const sent = all.filter((x) => x.v === 'enviado');
    const errored = all.filter((x) => isError(x.v));
    if (!pm && !fs && !ad) {
      steps.push({ key: 'people_mails', status: 'na', detail: '' });
    } else if (errored.length > 0) {
      steps.push({
        key: 'people_mails',
        status: 'error',
        detail: errored.map((x) => `${x.label}: ${x.v}`).join(' | '),
      });
    } else if (sent.length === all.length) {
      steps.push({ key: 'people_mails', status: 'ok', detail: '' });
    } else {
      steps.push({
        key: 'people_mails',
        status: 'scheduled',
        detail: sent.length ? `enviados: ${sent.map((x) => x.label).join(', ')}` : '',
      });
    }
  }

  // 3. Grupo de seguridad
  if (!pipeline) {
    steps.push({ key: 'security', status: 'na', detail: '' });
  } else if (pipeline.security_group?.startsWith('agregado')) {
    steps.push({ key: 'security', status: 'ok', detail: '' });
  } else if (pipeline.ev_email === 'enviado' || pipeline.ev_email === 'programado') {
    steps.push({ key: 'security', status: 'waiting', detail: '' });
  } else {
    steps.push({ key: 'security', status: 'na', detail: '' });
  }

  // 4. Apple Business (blueprint)
  if (!pipeline || !pipeline.abm_assignment) {
    steps.push({ key: 'apple', status: 'na', detail: '' });
  } else if (pipeline.abm_assignment === ABM_ASSIGNMENT.ASSIGNED) {
    steps.push({ key: 'apple', status: 'ok', detail: '' });
  } else if (pipeline.abm_assignment === ABM_ASSIGNMENT.ASSIGNED_NO_GROUP) {
    // En el blueprint pero en ningún grupo de usuarios de Apple Business: sin
    // plan, la Mac no enrola hasta que alguien lo agregue al grupo a mano.
    steps.push({
      key: 'apple',
      status: 'waiting',
      detail: i18n.t('users.onboarding_page.pipeline_detail.apple_no_group'),
    });
  } else if (isError(pipeline.abm_assignment)) {
    steps.push({ key: 'apple', status: 'error', detail: pipeline.abm_assignment });
  } else {
    steps.push({ key: 'apple', status: 'waiting', detail: '' });
  }

  // 5. Enrolamiento de la Mac
  if (!pipeline || !pipeline.enrollment_reminder) {
    steps.push({ key: 'enrollment', status: 'na', detail: '' });
  } else if (pipeline.enrollment_reminder.startsWith('no_necesario')) {
    steps.push({ key: 'enrollment', status: 'ok', detail: '' });
  } else if (pipeline.enrollment_reminder === 'enviado') {
    steps.push({ key: 'enrollment', status: 'scheduled', detail: 'recordatorio enviado' });
  } else {
    steps.push({ key: 'enrollment', status: 'waiting', detail: pipeline.enrollment_reminder });
  }

  return steps;
}

export function hasPipelineError(pipeline: AltaPipeline | null): boolean {
  if (!pipeline) return false;
  return (
    pipeline.estado === 'error' ||
    isError(pipeline.welcome_email) ||
    isError(pipeline.ev_email) ||
    isError(pipeline.abm_assignment) ||
    isError(pipeline.people_manager_mail ?? null) ||
    isError(pipeline.people_first_steps_mail ?? null) ||
    isError(pipeline.people_admission_mail ?? null)
  );
}

function StepChip({
  stepKey,
  status,
  detail,
}: {
  stepKey: string;
  status: StepStatus;
  detail: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const Icon = STEP_ICONS[stepKey];

  // Popover de Radix (portal): el tooltip no queda recortado por cards/dialogs
  // y auto-flipea de lado cuando no hay lugar.
  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <span
          className={`inline-flex h-7 w-7 cursor-default items-center justify-center rounded-md ${STEP_CLASSES[status]}`}
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <Icon className="h-4 w-4" />
        </span>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        sideOffset={6}
        className="w-60 p-3 text-left pointer-events-none"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <span className="flex items-center gap-2 font-semibold text-sm">
          <Icon className="h-3.5 w-3.5" />
          {t(`users.onboarding_page.pipeline.${stepKey}`)}
        </span>
        <span className="mt-1 block text-xs text-muted-foreground">
          {t(`users.onboarding_page.pipeline_desc.${stepKey}`)}
        </span>
        <span className="mt-2 flex items-center gap-1.5 text-xs font-medium">
          <span className={`inline-block h-2 w-2 rounded-full ${STATUS_DOT[status]}`} />
          {t(`users.onboarding_page.pipeline_status.${status}`)}
          {detail && <span className="text-muted-foreground font-normal">· {detail}</span>}
        </span>
      </PopoverContent>
    </Popover>
  );
}

const STATUS_DOT: Record<StepStatus, string> = {
  ok: 'bg-green-500',
  scheduled: 'bg-violet-500',
  waiting: 'bg-yellow-500',
  error: 'bg-red-500',
  na: 'bg-muted-foreground/40',
};

export function OnboardingPipeline({
  pipeline,
  hasCorreo,
}: {
  pipeline: AltaPipeline | null;
  hasCorreo: boolean;
}) {
  const steps = getPipelineSteps(pipeline, hasCorreo);

  return (
    <div className="flex items-center gap-1">
      {steps.map((step) => (
        <StepChip key={step.key} stepKey={step.key} status={step.status} detail={step.detail} />
      ))}
    </div>
  );
}

/** Compact color legend for the pipeline column */
export function PipelineLegend() {
  const { t } = useTranslation();
  const items: { status: StepStatus; dot: string }[] = [
    { status: 'ok', dot: 'bg-green-500' },
    { status: 'scheduled', dot: 'bg-violet-500' },
    { status: 'waiting', dot: 'bg-yellow-500' },
    { status: 'error', dot: 'bg-red-500' },
    { status: 'na', dot: 'bg-muted-foreground/40' },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
      {items.map((i) => (
        <span key={i.status} className="inline-flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${i.dot}`} />
          {t(`users.onboarding_page.pipeline_status.${i.status}`)}
        </span>
      ))}
    </div>
  );
}
